// src/components/kitchen/Kitchen3DView.js

import React, { useMemo } from "react";

import {
  Canvas,
} from "@react-three/fiber";

import {
  OrbitControls,
  Html,
  Grid,
  Edges,
} from "@react-three/drei";

import {
  buildKitchenGeometry,
} from "../../utils/kitchenGeometry";

import {
  buildKitchenModel,
} from "../../utils/kitchenModel";

/* =========================================================
   COLORS
   ========================================================= */

const WALL_COLOR =
  "#e9e6df";

const WALL_EDGE_COLOR =
  "#b8b3aa";

const CABINET_BODY_COLOR =
  "#d8d3c9";

const CABINET_FRONT_COLOR =
  "#fffdf8";

const CABINET_FRAME_COLOR =
  "#aaa49a";

const CABINET_EDGE_COLOR =
  "#8f8a82";

const COUNTER_COLOR =
  "#d1ccc2";

const COUNTER_EDGE_COLOR =
  "#918c84";

const HANDLE_COLOR =
  "#4f4b46";

const APPLIANCE_COLOR =
  "#a9adb0";

const APPLIANCE_DARK =
  "#25282b";

const SINK_COLOR =
  "#aeb2b5";

const SINK_DARK =
  "#4c5155";

const FLOOR_COLOR =
  "#c89f6a";

const FLOOR_LINE_COLOR =
  "#aa8155";

const BACKSPLASH_COLOR =
  "#e8e4dc";

/* =========================================================
   FORMATTERS
   ========================================================= */

function formatFeetInches(
  feet
) {
  const value =
    Number(feet);

  if (
    !Number.isFinite(value)
  ) {
    return "—";
  }

  let totalInches =
    Math.round(
      value * 12
    );

  const ft =
    Math.floor(
      totalInches / 12
    );

  totalInches -=
    ft * 12;

  if (
    totalInches === 12
  ) {
    return `${ft + 1}'`;
  }

  if (
    totalInches === 0
  ) {
    return `${ft}'`;
  }

  if (ft === 0) {
    return `${totalInches}"`;
  }

  return `${ft}'${totalInches}"`;
}

function formatWallLabel(
  wall
) {
  const length =
    formatFeetInches(
      wall.lengthFt
    );

  const height =
    formatFeetInches(
      wall.heightFt
    );

  const source =
    wall.heightSource ===
    "measured"
      ? "MEASURED"
      : "ASSUMED";

  return `${length} · H: ${height} · ${source}`;
}

/* =========================================================
   VIEW SETTINGS
   ========================================================= */

function getViewSettings(
  geometry
) {
  if (!geometry?.floor) {
    return {
      cameraPosition: [
        8,
        7,
        9,
      ],

      target: [
        2.5,
        2.5,
        2.5,
      ],
    };
  }

  const width =
    geometry.floor.widthFt;

  const depth =
    geometry.floor.depthFt;

  const centerX =
    width / 2;

  const centerZ =
    depth / 2;

  if (
    geometry.layout ===
    "u-shape"
  ) {
    const maxDimension =
      Math.max(
        width,
        depth
      );

    return {
      cameraPosition: [
        centerX +
          width * 0.55,

        9,

        depth +
          maxDimension *
            1.45,
      ],

      target: [
        centerX,
        2.8,
        centerZ * 0.55,
      ],
    };
  }

  return {
    cameraPosition: [
      width + 2.5,
      7,
      depth + 3,
    ],

    target: [
      centerX,
      2.8,
      centerZ,
    ],
  };
}

/* =========================================================
   FLOOR
   ========================================================= */

function Floor({
  floor,
}) {
  if (!floor) {
    return null;
  }

  const width =
    Math.max(
      Number(
        floor.widthFt
      ) || 0,
      0.01
    );

  const depth =
    Math.max(
      Number(
        floor.depthFt
      ) || 0,
      0.01
    );

  /*
   * kitchenGeometry defines the room footprint as:
   *
   * X: 0 → widthFt
   * Z: 0 → depthFt
   *
   * Every individual plank is explicitly clipped
   * to those boundaries before its mesh is created.
   */

  const PLANK_WIDTH_FT =
    0.5;

  const PLANK_LENGTH_FT =
    3.0;

  const FLOOR_THICKNESS_FT =
    0.06;

  const PLANK_THICKNESS_FT =
    0.025;

  const planks = [];

  let row = 0;

  for (
    let z0 = 0;
    z0 < depth - 0.0001;
    z0 += PLANK_WIDTH_FT
  ) {
    const z1 =
      Math.min(
        z0 +
          PLANK_WIDTH_FT,
        depth
      );

    const rowDepth =
      z1 - z0;

    if (
      rowDepth <= 0
    ) {
      continue;
    }

    const stagger =
      row % 2 === 0
        ? 0
        : PLANK_LENGTH_FT /
          2;

    let nominalStart =
      -stagger;

    let board = 0;

    while (
      nominalStart <
      width
    ) {
      const nominalEnd =
        nominalStart +
        PLANK_LENGTH_FT;

      const x0 =
        Math.max(
          0,
          nominalStart
        );

      const x1 =
        Math.min(
          width,
          nominalEnd
        );

      const boardLength =
        x1 - x0;

      if (
        boardLength >
        0.001
      ) {
        planks.push({
          key:
            `floor-${row}-${board}`,

          x:
            x0 +
            boardLength /
              2,

          z:
            z0 +
            rowDepth /
              2,

          length:
            boardLength,

          depth:
            rowDepth,
        });
      }

      nominalStart +=
        PLANK_LENGTH_FT;

      board += 1;
    }

    row += 1;
  }

  return (
    <group>
      <mesh
        position={[
          width / 2,

          -FLOOR_THICKNESS_FT /
            2,

          depth / 2,
        ]}
        receiveShadow
      >
        <boxGeometry
          args={[
            width,
            FLOOR_THICKNESS_FT,
            depth,
          ]}
        />

        <meshStandardMaterial
          color={
            FLOOR_COLOR
          }
          roughness={
            0.88
          }
        />
      </mesh>

      {planks.map(
        (plank) => (
          <mesh
            key={
              plank.key
            }
            position={[
              plank.x,

              PLANK_THICKNESS_FT /
                2,

              plank.z,
            ]}
            receiveShadow
          >
            <boxGeometry
              args={[
                plank.length,

                PLANK_THICKNESS_FT,

                plank.depth,
              ]}
            />

            <meshStandardMaterial
              color={
                FLOOR_COLOR
              }
              roughness={
                0.82
              }
            />

            <Edges
              threshold={
                15
              }
              color={
                FLOOR_LINE_COLOR
              }
            />
          </mesh>
        )
      )}
    </group>
  );
}

/* =========================================================
   WALL
   ========================================================= */

function Wall({
  wall,
}) {
  if (!wall) {
    return null;
  }

  return (
    <mesh
      position={
        wall.position
      }
      rotation={
        wall.rotation
      }
      receiveShadow
    >
      <boxGeometry
        args={[
          wall.lengthFt,
          wall.heightFt,
          wall.thicknessFt,
        ]}
      />

      <meshStandardMaterial
        color={
          WALL_COLOR
        }
        roughness={
          0.94
        }
      />

      <Edges
        threshold={
          15
        }
        color={
          WALL_EDGE_COLOR
        }
      />
    </mesh>
  );
}

/* =========================================================
   WALL DIMENSION LABEL
   ========================================================= */

function getWallLabelPosition(
  wall
) {
  const [
    x,
    ,
    z,
  ] = wall.position;

  const y =
    wall.heightFt +
    0.5;

  if (
    wall.id === "back"
  ) {
    return [
      x,
      y,
      z + 0.15,
    ];
  }

  if (
    wall.id === "left" ||
    wall.id === "side"
  ) {
    return [
      x + 0.15,
      y,
      z,
    ];
  }

  if (
    wall.id === "right"
  ) {
    return [
      x - 0.15,
      y,
      z,
    ];
  }

  return [
    x,
    y,
    z,
  ];
}

function WallLabel({
  wall,
}) {
  if (!wall) {
    return null;
  }

  const position =
    wall.labelPosition ||
    getWallLabelPosition(
      wall
    );

  return (
    <Html
      position={
        position
      }
      center
      distanceFactor={
        8
      }
      style={{
        pointerEvents:
          "none",
      }}
    >
      <div
        style={{
          background:
            "#0057b8",

          color:
            "#ffffff",

          padding:
            "7px 12px",

          borderRadius:
            "999px",

          fontSize:
            "11px",

          fontWeight:
            700,

          whiteSpace:
            "nowrap",

          textAlign:
            "center",

          lineHeight:
            1.35,

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.18)",

          border:
            "1px solid rgba(255,255,255,0.45)",
        }}
      >
        <div
          style={{
            fontSize:
              "15px",

            fontWeight:
              800,
          }}
        >
          {formatFeetInches(
            wall.lengthFt
          )}
        </div>

        <div
          style={{
            fontSize:
              "10px",

            marginTop:
              "1px",
          }}
        >
          {wall.label?.toUpperCase()}
        </div>

        <div
          style={{
            fontSize:
              "10px",

            opacity:
              0.92,

            marginTop:
              "1px",
          }}
        >
          H:{" "}
          {formatFeetInches(
            wall.heightFt
          )}{" "}
          ·{" "}
          {wall.heightSource ===
          "measured"
            ? "MEASURED"
            : "ASSUMED"}
        </div>
      </div>
    </Html>
  );
}

/* =========================================================
   HANDLE
   ========================================================= */

function Handle({
  position,
  horizontal = false,
}) {
  return (
    <mesh
      position={
        position
      }
      rotation={
        horizontal
          ? [
              0,
              0,
              Math.PI / 2,
            ]
          : [
              0,
              0,
              0,
            ]
      }
    >
      <boxGeometry
        args={[
          0.055,
          0.42,
          0.055,
        ]}
      />

      <meshStandardMaterial
        color={
          HANDLE_COLOR
        }
        metalness={
          0.55
        }
        roughness={
          0.32
        }
      />
    </mesh>
  );
}

/* =========================================================
   CABINET FRONT DIRECTION
   ========================================================= */

function getLocalFrontSign(
  frontDirection
) {
  switch (
    frontDirection
  ) {
    case "back":
    case "left":
      return -1;

    case "front":
    case "right":
    default:
      return 1;
  }
}

/* =========================================================
   SHAKER PANEL
   ========================================================= */

function ShakerPanel({
  width,
  height,
  position,
}) {
  const rail =
    Math.min(
      0.11,
      width * 0.12
    );

  const thickness =
    0.045;

  return (
    <group
      position={
        position
      }
    >
      <mesh>
        <boxGeometry
          args={[
            width,
            height,
            thickness,
          ]}
        />

        <meshStandardMaterial
          color={
            CABINET_FRONT_COLOR
          }
          roughness={
            0.7
          }
        />

        <Edges
          threshold={
            15
          }
          color={
            CABINET_EDGE_COLOR
          }
        />
      </mesh>

      <mesh
        position={[
          0,
          0,
          thickness / 2 +
            0.012,
        ]}
      >
        <boxGeometry
          args={[
            Math.max(
              width -
                rail * 2,
              0.05
            ),

            Math.max(
              height -
                rail * 2,
              0.05
            ),

            0.018,
          ]}
        />

        <meshStandardMaterial
          color="#efebe3"
          roughness={
            0.76
          }
        />
      </mesh>
    </group>
  );
}

/* =========================================================
   BASE CABINET FRONT
   ========================================================= */

function BaseCabinetFront({
  cabinet,
  frontSign,
}) {
  const width =
    cabinet.widthFt;

  const height =
    cabinet.heightFt;

  const depth =
    cabinet.depthFt;

  const frontZ =
    frontSign *
    (
      depth / 2 +
      0.075
    );

  const sideGap =
    Math.min(
      0.07,
      width * 0.045
    );

  const usableWidth =
    Math.max(
      width -
        sideGap * 2,
      0.1
    );

  const drawerHeight =
    0.48;

  const drawerY =
    height / 2 -
    drawerHeight / 2 -
    0.1;

  const lowerHeight =
    Math.max(
      height -
        drawerHeight -
        0.34,
      0.15
    );

  const lowerY =
    -height / 2 +
    lowerHeight / 2 +
    0.14;

  const doubleDoor =
    width >= 2.05;

  return (
    <group>
      <ShakerPanel
        width={
          usableWidth
        }
        height={
          drawerHeight
        }
        position={[
          0,
          drawerY,
          frontZ,
        ]}
      />

      <Handle
        position={[
          0,
          drawerY,
          frontZ +
            frontSign *
              0.075,
        ]}
        horizontal
      />

      {doubleDoor ? (
        <>
          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              lowerHeight
            }
            position={[
              -usableWidth /
                4,
              lowerY,
              frontZ,
            ]}
          />

          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              lowerHeight
            }
            position={[
              usableWidth /
                4,
              lowerY,
              frontZ,
            ]}
          />

          <Handle
            position={[
              -0.09,
              lowerY,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />

          <Handle
            position={[
              0.09,
              lowerY,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />
        </>
      ) : (
        <>
          <ShakerPanel
            width={
              usableWidth
            }
            height={
              lowerHeight
            }
            position={[
              0,
              lowerY,
              frontZ,
            ]}
          />

          <Handle
            position={[
              usableWidth *
                0.3,
              lowerY,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />
        </>
      )}

      <mesh
        position={[
          0,

          -height / 2 +
            0.13,

          frontSign *
            (
              depth / 2 -
              0.18
            ),
        ]}
      >
        <boxGeometry
          args={[
            width,
            0.26,
            0.24,
          ]}
        />

        <meshStandardMaterial
          color="#8f8980"
          roughness={
            0.92
          }
        />
      </mesh>
    </group>
  );
}

/* =========================================================
   WALL CABINET FRONT
   ========================================================= */

function WallCabinetFront({
  cabinet,
  frontSign,
}) {
  const width =
    cabinet.widthFt;

  const height =
    cabinet.heightFt;

  const depth =
    cabinet.depthFt;

  const frontZ =
    frontSign *
    (
      depth / 2 +
      0.07
    );

  const usableWidth =
    Math.max(
      width - 0.1,
      0.1
    );

  const usableHeight =
    Math.max(
      height - 0.1,
      0.1
    );

  const doubleDoor =
    width >= 1.8;

  return (
    <group>
      {doubleDoor ? (
        <>
          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              usableHeight
            }
            position={[
              -usableWidth /
                4,
              0,
              frontZ,
            ]}
          />

          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              usableHeight
            }
            position={[
              usableWidth /
                4,
              0,
              frontZ,
            ]}
          />

          <Handle
            position={[
              -0.085,
              0,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />

          <Handle
            position={[
              0.085,
              0,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />
        </>
      ) : (
        <>
          <ShakerPanel
            width={
              usableWidth
            }
            height={
              usableHeight
            }
            position={[
              0,
              0,
              frontZ,
            ]}
          />

          <Handle
            position={[
              usableWidth *
                0.3,
              0,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />
        </>
      )}
    </group>
  );
}

/* =========================================================
   TALL / PANTRY CABINET FRONT
   ========================================================= */

function TallCabinetFront({
  cabinet,
  frontSign,
}) {
  const width =
    cabinet.widthFt;

  const height =
    cabinet.heightFt;

  const depth =
    cabinet.depthFt;

  const frontZ =
    frontSign *
    (
      depth / 2 +
      0.075
    );

  const usableWidth =
    Math.max(
      width - 0.12,
      0.1
    );

  const usableHeight =
    Math.max(
      height - 0.34,
      0.5
    );

  const sectionGap =
    0.08;

  const topHeight =
    usableHeight *
    0.42;

  const bottomHeight =
    usableHeight -
    topHeight -
    sectionGap;

  const bottomY =
    -usableHeight / 2 +
    bottomHeight / 2;

  const topY =
    usableHeight / 2 -
    topHeight / 2;

  const doubleDoor =
    width >= 2.25;

  function PantrySection({
    sectionHeight,
    y,
  }) {
    if (
      doubleDoor
    ) {
      return (
        <>
          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              sectionHeight
            }
            position={[
              -usableWidth /
                4,
              y,
              frontZ,
            ]}
          />

          <ShakerPanel
            width={
              usableWidth /
                2 -
              0.035
            }
            height={
              sectionHeight
            }
            position={[
              usableWidth /
                4,
              y,
              frontZ,
            ]}
          />

          <Handle
            position={[
              -0.09,
              y,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />

          <Handle
            position={[
              0.09,
              y,
              frontZ +
                frontSign *
                  0.075,
            ]}
          />
        </>
      );
    }

    return (
      <>
        <ShakerPanel
          width={
            usableWidth
          }
          height={
            sectionHeight
          }
          position={[
            0,
            y,
            frontZ,
          ]}
        />

        <Handle
          position={[
            usableWidth *
              0.3,
            y,
            frontZ +
              frontSign *
                0.075,
          ]}
        />
      </>
    );
  }

  return (
    <group>
      <PantrySection
        sectionHeight={
          topHeight
        }
        y={
          topY
        }
      />

      <PantrySection
        sectionHeight={
          bottomHeight
        }
        y={
          bottomY
        }
      />

      <mesh
        position={[
          0,

          -height / 2 +
            0.13,

          frontSign *
            (
              depth / 2 -
              0.18
            ),
        ]}
      >
        <boxGeometry
          args={[
            width,
            0.26,
            0.24,
          ]}
        />

        <meshStandardMaterial
          color="#8f8980"
          roughness={
            0.92
          }
        />
      </mesh>
    </group>
  );
}

/* =========================================================
   UNDER-CABINET LIGHT
   ========================================================= */

function UnderCabinetLight({
  cabinet,
  frontSign,
}) {
  if (
    cabinet.type !==
    "wall"
  ) {
    return null;
  }

  return (
    <mesh
      position={[
        0,

        -cabinet.heightFt /
          2 -
          0.025,

        frontSign *
          (
            cabinet.depthFt /
              2 -
            0.04
          ),
      ]}
    >
      <boxGeometry
        args={[
          cabinet.widthFt *
            0.82,

          0.025,

          0.08,
        ]}
      />

      <meshStandardMaterial
        color="#fff5d8"
        emissive="#fff0c2"
        emissiveIntensity={
          0.85
        }
      />
    </mesh>
  );
}

/* =========================================================
   CABINET
   ========================================================= */

function Cabinet({
  cabinet,
}) {
  const frontSign =
    getLocalFrontSign(
      cabinet.frontDirection
    );

  const displayDepth =
    cabinet.type ===
    "wall"
      ? Math.min(
          cabinet.depthFt,
          0.95
        )
      : cabinet.depthFt;

  const displayCabinet = {
    ...cabinet,
    depthFt:
      displayDepth,
  };

  return (
    <group
      position={
        cabinet.position
      }
      rotation={
        cabinet.rotation
      }
    >
      <mesh
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            cabinet.widthFt,
            cabinet.heightFt,
            displayDepth,
          ]}
        />

        <meshStandardMaterial
          color={
            CABINET_BODY_COLOR
          }
          roughness={
            0.82
          }
        />

        <Edges
          threshold={
            15
          }
          color={
            CABINET_EDGE_COLOR
          }
        />
      </mesh>

      <mesh
        position={[
          0,
          0,

          frontSign *
            (
              displayDepth /
                2 +
              0.025
            ),
        ]}
      >
        <boxGeometry
          args={[
            Math.max(
              cabinet.widthFt -
                0.03,
              0.05
            ),

            Math.max(
              cabinet.heightFt -
                0.03,
              0.05
            ),

            0.035,
          ]}
        />

        <meshStandardMaterial
          color={
            CABINET_FRAME_COLOR
          }
          roughness={
            0.82
          }
        />
      </mesh>

      {cabinet.type ===
      "wall" ? (
        <WallCabinetFront
          cabinet={
            displayCabinet
          }
          frontSign={
            frontSign
          }
        />
      ) : cabinet.type ===
        "tall" ? (
        <TallCabinetFront
          cabinet={
            displayCabinet
          }
          frontSign={
            frontSign
          }
        />
      ) : (
        <BaseCabinetFront
          cabinet={
            displayCabinet
          }
          frontSign={
            frontSign
          }
        />
      )}

      <UnderCabinetLight
        cabinet={
          displayCabinet
        }
        frontSign={
          frontSign
        }
      />
    </group>
  );
}

/* =========================================================
   COUNTERTOP
   ========================================================= */

function Countertop({
  counter,
}) {
  return (
    <mesh
      position={
        counter.position
      }
      rotation={
        counter.rotation
      }
      castShadow
      receiveShadow
    >
      <boxGeometry
        args={[
          counter.widthFt,

          counter.thicknessFt,

          counter.depthFt +
            0.13,
        ]}
      />

      <meshStandardMaterial
        color={
          COUNTER_COLOR
        }
        roughness={
          0.34
        }
        metalness={
          0.02
        }
      />

      <Edges
        threshold={
          15
        }
        color={
          COUNTER_EDGE_COLOR
        }
      />
    </mesh>
  );
}

/* =========================================================
   BACKSPLASH
   ========================================================= */

function Backsplash({
  counter,
}) {
  const height =
    1.45;

  /*
   * Countertops now carry wall + frontDirection metadata.
   *
   * The backsplash belongs on the WALL side of the
   * countertop — opposite the cabinet/front side.
   *
   * The calculation is performed in the counter's LOCAL
   * coordinate system. This means the same logic works
   * after left/right counters are rotated 90 degrees.
   */

  const frontSign =
    getLocalFrontSign(
      counter.frontDirection
    );

  const wallSign =
    -frontSign;

  const backsplashZ =
    wallSign *
    (
      counter.depthFt / 2 -
      0.04
    );

  /*
   * Decorative horizontal lines are moved slightly
   * toward the room/front side of the backsplash.
   * This keeps them visible and prevents z-fighting.
   */

  const groutZ =
    backsplashZ +
    frontSign * 0.032;

  return (
    <group
      position={
        counter.position
      }
      rotation={
        counter.rotation
      }
    >
      <mesh
        position={[
          0,

          height / 2 +
            0.09,

          backsplashZ,
        ]}
      >
        <boxGeometry
          args={[
            counter.widthFt,
            height,
            0.05,
          ]}
        />

        <meshStandardMaterial
          color={
            BACKSPLASH_COLOR
          }
          roughness={
            0.82
          }
        />

        <Edges
          threshold={
            15
          }
          color="#cbc5ba"
        />
      </mesh>

      {[
        0.35,
        0.7,
        1.05,
      ].map(
        (y) => (
          <mesh
            key={
              `backsplash-${counter.id}-${y}`
            }
            position={[
              0,

              y + 0.08,

              groutZ,
            ]}
          >
            <boxGeometry
              args={[
                counter.widthFt,
                0.015,
                0.012,
              ]}
            />

            <meshStandardMaterial
              color="#c7c0b5"
              roughness={
                1
              }
            />
          </mesh>
        )
      )}
    </group>
  );
}

/* =========================================================
   RANGE
   ========================================================= */

function Burner({
  x,
  z,
  topY,
}) {
  return (
    <mesh
      position={[
        x,
        topY,
        z,
      ]}
      rotation={[
        -Math.PI / 2,
        0,
        0,
      ]}
    >
      <ringGeometry
        args={[
          0.14,
          0.21,
          28,
        ]}
      />

      <meshStandardMaterial
        color="#565b5f"
        metalness={
          0.72
        }
        roughness={
          0.25
        }
      />
    </mesh>
  );
}

function Range({
  appliance,
}) {
  const width =
    appliance.widthFt;

  const height =
    appliance.heightFt;

  const depth =
    appliance.depthFt;

  const frontZ =
    depth / 2 +
    0.055;

  const topY =
    height / 2 +
    0.04;

  return (
    <group
      position={
        appliance.position
      }
      rotation={
        appliance.rotation
      }
    >
      <mesh
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            width,
            height,
            depth,
          ]}
        />

        <meshStandardMaterial
          color={
            APPLIANCE_COLOR
          }
          metalness={
            0.75
          }
          roughness={
            0.27
          }
        />

        <Edges
          threshold={
            15
          }
          color="#666b6e"
        />
      </mesh>

      {/* Oven glass */}
      <mesh
        position={[
          0,

          -height *
            0.12,

          frontZ,
        ]}
      >
        <boxGeometry
          args={[
            width *
              0.82,

            height *
              0.46,

            0.06,
          ]}
        />

        <meshStandardMaterial
          color={
            APPLIANCE_DARK
          }
          metalness={
            0.22
          }
          roughness={
            0.19
          }
        />

        <Edges
          threshold={
            15
          }
          color="#73777a"
        />
      </mesh>

      {/* Oven handle */}
      <mesh
        position={[
          0,

          height *
            0.12,

          frontZ +
            0.07,
        ]}
      >
        <boxGeometry
          args={[
            width *
              0.68,

            0.065,

            0.065,
          ]}
        />

        <meshStandardMaterial
          color="#d0d2d3"
          metalness={
            0.92
          }
          roughness={
            0.18
          }
        />
      </mesh>

      {/* Control panel */}
      <mesh
        position={[
          0,

          height *
            0.32,

          frontZ,
        ]}
      >
        <boxGeometry
          args={[
            width *
              0.9,

            0.32,

            0.06,
          ]}
        />

        <meshStandardMaterial
          color="#777c7f"
          metalness={
            0.6
          }
          roughness={
            0.25
          }
        />
      </mesh>

      {/* Cooktop */}
      <mesh
        position={[
          0,
          topY,
          0,
        ]}
      >
        <boxGeometry
          args={[
            width *
              0.94,

            0.06,

            depth *
              0.92,
          ]}
        />

        <meshStandardMaterial
          color="#141617"
          roughness={
            0.2
          }
        />
      </mesh>

      <Burner
        x={
          -width *
          0.23
        }
        z={
          -depth *
          0.22
        }
        topY={
          topY +
          0.04
        }
      />

      <Burner
        x={
          width *
          0.23
        }
        z={
          -depth *
          0.22
        }
        topY={
          topY +
          0.04
        }
      />

      <Burner
        x={
          -width *
          0.23
        }
        z={
          depth *
          0.2
        }
        topY={
          topY +
          0.04
        }
      />

      <Burner
        x={
          width *
          0.23
        }
        z={
          depth *
          0.2
        }
        topY={
          topY +
          0.04
        }
      />
    </group>
  );
}

/* =========================================================
   SINK
   ========================================================= */

function Sink({
  fixture,
}) {
  return (
    <group
      position={
        fixture.position
      }
      rotation={
        fixture.rotation
      }
    >
      {/* Sink rim */}
      <mesh>
        <boxGeometry
          args={[
            fixture.widthFt,
            0.065,
            fixture.depthFt,
          ]}
        />

        <meshStandardMaterial
          color={
            SINK_COLOR
          }
          metalness={
            0.8
          }
          roughness={
            0.21
          }
        />

        <Edges
          threshold={
            15
          }
          color="#757a7d"
        />
      </mesh>

      {/* Basin */}
      <mesh
        position={[
          0,
          -0.13,
          0,
        ]}
      >
        <boxGeometry
          args={[
            fixture.widthFt *
              0.76,

            0.22,

            fixture.depthFt *
              0.72,
          ]}
        />

        <meshStandardMaterial
          color={
            SINK_DARK
          }
          metalness={
            0.68
          }
          roughness={
            0.26
          }
        />
      </mesh>

      {/* Faucet base */}
      <mesh
        position={[
          0,
          0.08,

          -fixture.depthFt *
            0.37,
        ]}
      >
        <cylinderGeometry
          args={[
            0.09,
            0.09,
            0.1,
            22,
          ]}
        />

        <meshStandardMaterial
          color="#c4c7c9"
          metalness={
            0.92
          }
          roughness={
            0.17
          }
        />
      </mesh>

      {/* Faucet stem */}
      <mesh
        position={[
          0,
          0.42,

          -fixture.depthFt *
            0.37,
        ]}
      >
        <cylinderGeometry
          args={[
            0.045,
            0.045,
            0.7,
            18,
          ]}
        />

        <meshStandardMaterial
          color="#c4c7c9"
          metalness={
            0.92
          }
          roughness={
            0.17
          }
        />
      </mesh>

      {/* Faucet spout */}
      <mesh
        position={[
          0,
          0.72,

          -fixture.depthFt *
            0.22,
        ]}
        rotation={[
          Math.PI / 2,
          0,
          0,
        ]}
      >
        <cylinderGeometry
          args={[
            0.045,
            0.045,
            0.3,
            18,
          ]}
        />

        <meshStandardMaterial
          color="#c4c7c9"
          metalness={
            0.92
          }
          roughness={
            0.17
          }
        />
      </mesh>
    </group>
  );
}

/* =========================================================
   GENERATED MODEL
   ========================================================= */

function GeneratedKitchen({
  model,
}) {
  return (
    <>
      {model.cabinets.map(
        (cabinet) => (
          <Cabinet
            key={
              cabinet.id
            }
            cabinet={
              cabinet
            }
          />
        )
      )}

      {model.countertops.map(
        (counter) => (
          <React.Fragment
            key={
              counter.id
            }
          >
            <Countertop
              counter={
                counter
              }
            />

            <Backsplash
              counter={
                counter
              }
            />
          </React.Fragment>
        )
      )}

      {model.appliances.map(
        (appliance) => {
          if (
            appliance.type ===
            "range"
          ) {
            return (
              <Range
                key={
                  appliance.id
                }
                appliance={
                  appliance
                }
              />
            );
          }

          return null;
        }
      )}

      {model.fixtures.map(
        (fixture) => {
          if (
            fixture.type ===
            "sink"
          ) {
            return (
              <Sink
                key={
                  fixture.id
                }
                fixture={
                  fixture
                }
              />
            );
          }

          return null;
        }
      )}
    </>
  );
}

/* =========================================================
   SCENE
   ========================================================= */

function KitchenScene({
  geometry,
  model,
}) {
  const view =
    getViewSettings(
      geometry
    );

  return (
    <>
      <ambientLight
        intensity={
          1.15
        }
      />

      <directionalLight
        position={[
          6,
          12,
          8,
        ]}
        intensity={
          0.95
        }
      />

      <directionalLight
        position={[
          -6,
          8,
          4,
        ]}
        intensity={
          0.4
        }
      />

      <Floor
        floor={
          geometry.floor
        }
      />

      {geometry.walls.map(
        (wall) => (
          <React.Fragment
            key={
              wall.id
            }
          >
            <Wall
              wall={
                wall
              }
            />

            <WallLabel
              wall={
                wall
              }
            />
          </React.Fragment>
        )
      )}

      <GeneratedKitchen
        model={
          model
        }
      />

      <Grid
        position={[
          geometry.floor
            ?.widthFt /
            2 || 0,

          -0.055,

          geometry.floor
            ?.depthFt /
            2 || 0,
        ]}
        args={[
          Math.max(
            geometry.floor
              ?.widthFt ||
              10,
            10
          ),

          Math.max(
            geometry.floor
              ?.depthFt ||
              10,
            10
          ),
        ]}
        cellSize={
          1
        }
        cellThickness={
          0.35
        }
        sectionSize={
          5
        }
        sectionThickness={
          0.6
        }
        fadeDistance={
          30
        }
        fadeStrength={
          1
        }
        infiniteGrid={
          false
        }
      />

      <OrbitControls
        makeDefault
        target={
          view.target
        }
        enableDamping
        dampingFactor={
          0.08
        }
        minDistance={
          3
        }
        maxDistance={
          35
        }
        maxPolarAngle={
          Math.PI /
          2.02
        }
      />
    </>
  );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function Kitchen3DView({
  layout,
  walls,
}) {
  const geometry =
    useMemo(
      () =>
        buildKitchenGeometry({
          layout,
          walls,
        }),
      [
        layout,
        walls,
      ]
    );

  const model =
    useMemo(
      () =>
        buildKitchenModel(
          geometry
        ),
      [
        geometry,
      ]
    );

  const view =
    useMemo(
      () =>
        getViewSettings(
          geometry
        ),
      [
        geometry,
      ]
    );

  if (
    !geometry ||
    !geometry.walls ||
    geometry.walls.length ===
      0
  ) {
    return (
      <div
        style={{
          padding:
            "24px",

          border:
            "1px solid #dfe5ec",

          borderRadius:
            "12px",

          background:
            "#f8fafc",

          color:
            "#51606f",
        }}
      >
        Complete the wall measurements to generate the 3D site survey.
      </div>
    );
  }

  return (
    <div
      style={{
        width:
          "100%",

        height:
          "560px",

        minHeight:
          "420px",

        borderRadius:
          "14px",

        overflow:
          "hidden",

        border:
          "1px solid #dfe5ec",

        background:
          "linear-gradient(180deg, #f8fafc 0%, #eef2f5 100%)",
      }}
    >
      <Canvas
        shadows
        camera={{
          position:
            view.cameraPosition,

          fov:
            45,

          near:
            0.1,

          far:
            100,
        }}
        gl={{
          antialias:
            true,

          alpha:
            true,
        }}
      >
        <KitchenScene
          geometry={
            geometry
          }
          model={
            model
          }
        />
      </Canvas>
    </div>
  );
}
